// ───────────────────────────────────────────────────
// src/features/stock/api/productApi.ts
// ───────────────────────────────────────────────────

import { fetchData } from '../../../../utils/fetch';

/**
 * Product shape
 */
export interface Product {
  id_product: number;
  product_name: string;
  product_sku?: string;
  brand_name?: string;
  product_price_purchase?: number;
  product_price_sales?: number;
  timestamp?: string;
}

/**
 * Fetch Products API
 * Hits GET /service_product_manager/get_products on the backend.
 */
export async function fetchProductsApi(): Promise<Product[]> {
  const raw = await fetchData('/service_product_manager/get_products', { method: 'GET' });

  if (Array.isArray(raw)) {
    return raw as Product[];
  }

  if (raw != null && Array.isArray((raw as any).data)) {
    return (raw as any).data.map((rec: any) => ({
      id_product: rec.product_id,
      product_name: rec.product_name,
      product_sku: rec.product_sku,
      brand_name: rec.brand_name,
      product_price_purchase: rec.product_price_purchase,
      product_price_sales: rec.product_price_sales,
      timestamp: rec.timestamp?.split('.')[0],
    }));
  }

  if (raw != null && Array.isArray((raw as any).result)) {
    return (raw as any).result.map((rec: any) => ({
      id_product: rec.product_id,
      product_name: rec.product_name,
      product_sku: rec.product_sku,
      brand_name: rec.brand_name,
      product_price_purchase: rec.product_price_purchase,
      product_price_sales: rec.product_price_sales,
      timestamp: rec.timestamp?.split('.')[0],
    }));
  }

  console.warn('[fetchProductsApi] Unexpected response shape:', raw);
  return [];
}

/** 
 * Payload to create a new product
 */
export interface NewProductPayload {
  product_sku: string;
  product_name: string;
  unit_of_measure: 'each' | 'kg' | 'l';
  brand_name: string;
  product_price_purchase: number;
  product_price_sales: number;
}

/**
 * Creates a new product via POST /service_product_manager/create_product
 */
export async function createProductApi(
  payload: NewProductPayload
): Promise<Product> {
  try {
    const response = await fetchData('/service_product_manager/create_product', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (response && typeof response === 'object' && 'error' in response) {
      throw new Error((response as any).error || 'Unknown API error');
    }

    if (response.created && typeof response.created.product_id === 'number') {
      return {
        id_product: response.created.product_id,
        product_name: response.created.product_name,
        product_sku: response.created.product_sku,
        brand_name: response.created.brand_name,
        product_price_purchase: response.created.product_price_purchase,
        product_price_sales: response.created.product_price_sales,
        timestamp: response.created.timestamp?.split('.')[0] ?? '',
      };
    }

    if (response.result && typeof response.result.id_product === 'number') {
      return {
        id_product: response.result.id_product,
        product_name: response.result.product_name,
        product_sku: response.result.product_sku,
        brand_name: response.result.brand_name,
        product_price_purchase: response.result.product_price_purchase,
        product_price_sales: response.result.product_price_sales,
        timestamp: response.result.timestamp?.split('.')[0] ?? '',
      };
    }

    throw new Error('Could not parse newly-created product.');
  } catch (err) {
    console.error('[createProductApi] Unexpected error:', err);
    throw err;
  }
}

/**
 * Payload to delete a product
 */
export interface DeleteProductPayload {
  id: number;
}

/**
 * Calls POST /service_product_manager/delete_product → returns void.
 */
export async function deleteProductApi(
  payload: DeleteProductPayload
): Promise<void> {
  await fetchData('/service_product_manager/delete_product', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_id: payload.id }),
  });
}

/**
 * Payload to update a product
 */
export interface UpdateProductPayload {
  id: number;
  product_sku?: string;
  product_name?: string;
  unit_of_measure?: 'each' | 'kg' | 'l';
  brand_name?: string;
  product_price_purchase?: number;
  product_price_sales?: number;
}

/**
 * Calls PUT /service_product_manager/update_product → returns void.
 */
export async function updateProductApi(
  payload: UpdateProductPayload
): Promise<void> {
  const body: any = {
    product_id: payload.id,
    ...(payload.product_sku != null && { product_sku: payload.product_sku }),
    ...(payload.product_name != null && { product_name: payload.product_name }),
    ...(payload.unit_of_measure != null && { unit_of_measure: payload.unit_of_measure }),
    ...(payload.brand_name != null && { brand_name: payload.brand_name }),
    ...(payload.product_price_purchase != null && { product_price_purchase: payload.product_price_purchase }),
    ...(payload.product_price_sales != null && { product_price_sales: payload.product_price_sales }),
  };

  await fetchData('/service_product_manager/update_product', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Map of product→[attribute_id]
 */
export interface ProductAttributesMap {
  [productId: number]: number[];
}

export async function fetchProductAttributesMap(): Promise<ProductAttributesMap> {
  const response = await fetchData('/service_product_manager/get_product_attributes', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (response && typeof response === 'object' && (response as any).product_attributes) {
    const rawMap = (response as any).product_attributes as Record<string, any>;
    const result: ProductAttributesMap = {};
    for (const key in rawMap) {
      result[Number(key)] = Array.isArray(rawMap[key]) ? rawMap[key].map((x: any) => Number(x)) : [];
    }
    return result;
  }

  console.warn('[fetchProductAttributesMap] Unexpected response shape:', response);
  return {};
}

export interface LinkAttributePayload {
  product_id: number;
  attribute_id: number;
  unlink?: boolean;
}

export async function linkAttributeApi(
  payload: LinkAttributePayload
): Promise<{ product_id: number; attribute_id: number; unlinked: boolean }> {
  const response = await fetchData('/service_product_manager/link_attribute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (
    response &&
    typeof response === 'object' &&
    typeof (response as any).product_id === 'number' &&
    typeof (response as any).attribute_id === 'number'
  ) {
    return {
      product_id: (response as any).product_id,
      attribute_id: (response as any).attribute_id,
      unlinked: Boolean((response as any).unlinked),
    };
  }

  throw new Error('Could not parse link_attribute response.');
}
